import { useRef, useState } from "react";
import type { FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dagjjs1tto0ceqqnht50";

const benefits = [
  { icon: "ri-mail-send-line", text: "每周一封，精选一周热点与复盘，不刷屏" },
  { icon: "ri-shield-check-line", text: "只发干货，绝不贩卖焦虑或低质搬运" },
  { icon: "ri-user-smile-line", text: "随时可退订，你的邮箱只归你管" },
];

export default function Subscribe() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐拦截
    const honeypot = (data.get("contact_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，欢迎加入瓜田速报！");
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = parsed?.code;
      const serverMsg =
        (meta?.message as string) || (meta?.detail as string) || (parsed?.message as string) || "";

      if (res.ok && code === "OK") {
        setStatus("success");
        setMessage("订阅成功，欢迎加入瓜田速报！每周与你准时相见。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交未成功，请稍后再试，或换一个邮箱重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请检查连接后重试。");
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-28 bg-paper-100">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="overflow-hidden rounded-xl bg-primary-600">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
            {/* 左侧文案 */}
            <div className="relative flex flex-col justify-center p-6 text-white md:p-10">
              <span className="absolute -right-8 -top-8 text-[120px] text-white/10"><i className="ri-mail-star-line" /></span>
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-accent-100">
                <i className="ri-mail-open-line" />
                WEEKLY DIGEST
              </div>
              <h2 className="font-serif text-2xl font-extrabold leading-tight text-white md:text-3xl">订阅「瓜田周报」</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-white/85">
                每周日，把一周热度事件、复盘专题与辟谣报告整理成一份清爽摘要，发到你的邮箱。不追更也不用怕错过。
              </p>
              <ul className="mt-6 space-y-2.5">
                {benefits.map((b) => (
                  <li key={b.text} className="flex items-start gap-2.5 text-sm text-white/90">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <i className={`${b.icon} text-xs`} />
                    </span>
                    {b.text}
                  </li>
                ))}
              </ul>
            </div>

            {/* 右侧表单 */}
            <div className="bg-white p-6 md:p-10">
              <h3 className="font-serif text-lg font-bold text-stone-900">填个邮箱，瓜田送上门</h3>
              <form
                ref={formRef}
                data-readdy-form="newsletter-subscribe"
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-stone-700">常用邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-paper-200 bg-paper-50 px-4 py-3 text-sm text-stone-800 outline-none transition placeholder:text-stone-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <span className="mb-1.5 block text-sm font-medium text-stone-700">你最想订阅哪些内容？</span>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "all", label: "全部精选" },
                      { v: "hot", label: "热点速览" },
                      { v: "recap", label: "复盘专题" },
                      { v: "verify", label: "辟谣求真" },
                    ].map((o) => (
                      <label key={o.v} className="flex cursor-pointer items-center">
                        <input type="radio" name="pref" value={o.v} defaultChecked={o.v === "all"} className="peer sr-only" />
                        <span className="rounded-full border border-paper-200 px-3.5 py-1.5 text-sm text-stone-600 transition peer-checked:border-primary-500 peer-checked:bg-primary-50 peer-checked:text-primary-700">
                          {o.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 蜜罐隐藏字段 */}
                <label htmlFor="contact_alt">备用联系方式</label>
                <input
                  id="contact_alt"
                  name="contact_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md bg-leaf-50 px-4 py-3 text-sm text-leaf-700">
                    <i className="ri-checkbox-circle-line" />{message}
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
                    <i className="ri-error-warning-line mt-0.5" />{message}
                  </p>
                )}
                {status === "loading" && (
                  <p className="flex items-center gap-2 rounded-md bg-paper-100 px-4 py-3 text-sm text-stone-600">
                    <i className="ri-loader-4-line animate-spin" />正在提交…
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i className="ri-send-plane-line" />
                  立即订阅
                </button>
                <p className="text-center text-[11px] text-stone-400">提交即代表同意接收本站邮件，我们不会向第三方共享你的信息。</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}